import React, { useCallback, useEffect, useState } from 'react';
import { formatBusiness } from 'config/time';
import { useParams } from 'react-router-dom';
import axiosInstance from 'config/axiosInstance';
import true_icon from '../../../../assets/icons/true.svg';
import false_icon from '../../../../assets/icons/false.svg';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import FetchFailed from 'reusable/FetchFailed';
import DeviceTimeZone, { deviceTimeZoneOf } from 'reusable/DeviceTimeZone';
import { healthQuestionLabel } from 'config/healthQuestions';

const DriverHealthCheckDetails: React.FC = () => {
  const { t, i18n } = useTranslation('driver');
  const { id } = useParams<{ id: string }>();
  const [healthCheckDetails, setHealthCheckDetails] = useState<any>(null);
  // ADM-15。取得の失敗を「読み込み中…」のままにせず、理由と再読み込みを出す。
  const [fetchError, setFetchError] = useState<unknown>(null);

  // 認証（access の付与・Cookie での取り直し）は axiosInstance が行う（ADM-17）。
  const fetchHealthCheckDetails = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/v1/health_inspection_records/${id}/`);
      setHealthCheckDetails(response.data);
      setFetchError(null);
    } catch (error) {
      console.error('Failed to fetch health check details', error);
      setFetchError(error);
    }
  }, [id]);

  useEffect(() => {
    fetchHealthCheckDetails();
  }, [fetchHealthCheckDetails]);

  if (fetchError) {
    return (
      <FetchFailed
        error={fetchError}
        onRetry={fetchHealthCheckDetails}
        message={t('ui.detail-fetch-failed', { ns: 'common' })}
      />
    );
  }

  if (!healthCheckDetails) {
    return <div>{t('ui.loading', { ns: 'common' })}</div>;
  }

  return (
    <section className="p-4 bg-gray-100">
      <div className="flex flex-row mb-4 text-primary/300 font-semi-bold text-subtitle-2 items-center space-x-4">
        <p>{healthCheckDetails.id}</p>
        <MdKeyboardArrowRight size={24} />
        <p>{t('view.health-check.title')}</p>
        <MdKeyboardArrowRight size={24} />
        <p className="underline">{t('view.health-check.detail_button')}</p>
      </div>
      <div className="mb-4 bg-white shadow-md rounded-lg p-4">
        <div className="grid grid-cols-5 gap-4">
          <div>
            <p className="font-semibold">
              {t('view.health-check.health-check-detail.status-date')}
            </p>
            <p>
              {/* EV-116。端末の時間帯・言語の書式ではなく、業務の時間帯で表と同じ書式にする。 */}
              {formatBusiness(
                healthCheckDetails.create_time,
                i18n.language === 'jp' ? 'yyyy/MM/dd' : 'dd MMM yyyy'
              )}
            </p>
          </div>
          <div>
            <p className="font-semibold">
              {t('view.health-check.health-check-detail.status-driver-name')}
            </p>
            {/* EV-338（2026-10-14 PM 判断）。名前が空（user が無い）なら、見た目は「-」（EV-311）、読み上げは「名前のない運転者」。 */}
            <p>
              {healthCheckDetails.user?.name || (
                <>
                  <span aria-hidden="true">-</span>
                  <span className="sr-only">{t('ui.unnamed-driver', { ns: 'common' })}</span>
                </>
              )}
            </p>
          </div>
          <div>
            <p className="font-semibold">
              {t('view.health-check.health-check-detail.status-maintenance')}
            </p>
            <p>{healthCheckDetails.maintenance_manager}</p>
          </div>
          <div>
            <p className="font-semibold">
              {t('view.health-check.health-check-detail.status-operation')}
            </p>
            <p>{healthCheckDetails.operations_manager}</p>
          </div>
          {/* EV-116 段階3。送信の時点の端末の時間帯（詳細では名前も並べる）。 */}
          <div>
            <p className="font-semibold">{t('time-zone.device', { ns: 'common' })}</p>
            <p>
              <DeviceTimeZone {...deviceTimeZoneOf(healthCheckDetails)} at={healthCheckDetails.create_time} detail />
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white shadow-md rounded-lg p-4">
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border px-4 py-2 text-left">
                {t('view.health-check.health-check-detail.question')}
              </th>
              <th className="border px-4 py-2 text-left">
                {t('view.health-check.health-check-detail.answer')}
              </th>
              <th className="border px-4 py-2 text-left">
                {t('view.health-check.health-check-detail.note')}
              </th>
            </tr>
          </thead>
          <tbody>
            {healthCheckDetails.details.driver_healthcheck[0].item.map(
              (item: any, index: number) => (
                <tr key={index} className="border-b">
                  {/* アプリは質問を英語の文言で送る。車両点検と同じく表示で画面の言語に置き換える。 */}
                  <td className=" px-4 py-2">{healthQuestionLabel(item.name, t)}</td>
                  <td className=" px-4 py-2 text-center">
                    {item.status ? (
                      <img src={true_icon} alt="" />
                    ) : (
                      <img src={false_icon} alt="" />
                    )}
                  </td>
                  <td className=" px-4 py-2">{item.note || '-'}</td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default DriverHealthCheckDetails;
